﻿/*!
    *
    * Wijmo Library 5.20251.34
    * https://developer.mescius.com/wijmo
    *
    * Copyright(c) MESCIUS inc. All rights reserved.
    *
    * Licensed under the End-User License Agreement For MESCIUS Wijmo Software.
    * us.sales@mescius.com
    * https://developer.mescius.com/wijmo/licensing
    *
    */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { VueApi, WjComponentBehavior } from '@mescius/wijmo.vue2.base';
import * as wjcGridSheet from '@mescius/wijmo.grid.sheet';
var WjFlexSheetBehavior = /** @class */ (function (_super) {
    __extends(WjFlexSheetBehavior, _super);
    function WjFlexSheetBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFlexSheetBehavior.tag = 'wj-flex-sheet';
    WjFlexSheetBehavior.props = [
        'isDisabled',
        'tabOrder',
        'newRowAtTop',
        'allowAddNew',
        'allowDelete',
        'allowDragging',
        'allowMerging',
        'allowResizing',
        'allowSorting',
        'allowPinning',
        'autoScroll',
        'autoRowHeights',
        'autoSizeMode',
        'autoGenerateColumns',
        'autoSearch',
        'caseSensitiveSearch',
        'quickAutoSize',
        'bigCheckboxes',
        'childItemsPath',
        'groupHeaderFormat',
        'headersVisibility',
        'showSelectedHeaders',
        'showMarquee',
        'showPlaceholders',
        'itemFormatter',
        'isReadOnly',
        'imeEnabled',
        'mergeManager',
        'selectionMode',
        'showGroups',
        'showSort',
        'showDropDown',
        'showAlternatingRows',
        'showErrors',
        'alternatingRowStep',
        'itemValidator',
        'validateEdits',
        'treeIndent',
        'itemsSource',
        'autoClipboard',
        'expandSelectionOnCopyPaste',
        'frozenRows',
        'frozenColumns',
        'cloneFrozenCells',
        'deferResizing',
        'sortRowIndex',
        'editColumnIndex',
        'stickyHeaders',
        'preserveSelectedState',
        'preserveOutlineState',
        'preserveWhiteSpace',
        'keyActionTab',
        'keyActionEnter',
        'rowHeaderPath',
        'virtualizationThreshold',
        'anchorCursor',
        'lazyRender',
        'refreshOnEdit',
        'copyHeaders',
        'columnGroups',
        'allowAutoFill',
        'isTabHolderVisible',
        'showFilterIcons',
        'enableDragDrop',
        'enableFormulas',
        'selectedSheetIndex',
        'commitEmptyEdits',
        'headersFocusability',
    ];
    WjFlexSheetBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'beginningEdit',
        'cellEditEnded',
        'cellEditEnding',
        'prepareCellForEdit',
        'formatItem',
        'resizingColumn',
        'resizedColumn',
        'autoSizingColumn',
        'autoSizedColumn',
        'draggingColumn',
        'draggingColumnOver',
        'draggedColumn',
        'sortingColumn',
        'sortedColumn',
        'pinningColumn',
        'pinnedColumn',
        'resizingRow',
        'resizedRow',
        'autoSizingRow',
        'autoSizedRow',
        'draggingRow',
        'draggingRowOver',
        'draggedRow',
        'deletingRow',
        'deletedRow',
        'loadingRows',
        'loadedRows',
        'rowEditStarting',
        'rowEditStarted',
        'rowEditEnding',
        'rowEditEnded',
        'rowAdded',
        'groupCollapsedChanging',
        'groupCollapsedChanged',
        'columnGroupCollapsedChanging',
        'columnGroupCollapsedChanged',
        'itemsSourceChanging',
        'itemsSourceChanged',
        'selectionChanging',
        'selectionChanged',
        'scrollPositionChanged',
        'updatingView',
        'updatedView',
        'updatingLayout',
        'updatedLayout',
        'pasting',
        'pasted',
        'pastingCell',
        'pastedCell',
        'copying',
        'copied',
        'draggingRowColumn',
        'droppingRowColumn',
        'beginDroppingRowColumn',
        'endDroppingRowColumn',
        'loaded',
        'unknownFunction',
        'sheetCleared',
        'prepareChangingRow',
        'prepareChangingColumn',
        'rowChanged',
        'columnChanged',
        'autoFilling',
        'autoFilled'
    ];
    WjFlexSheetBehavior.changeEvents = {
        'selectedSheetChanged': ['selectedSheetIndex'],
    };
    WjFlexSheetBehavior.classCtor = function () { return wjcGridSheet.FlexSheet; };
    return WjFlexSheetBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.grid.sheet.FlexSheet} control.
 *
 * The <b>wj-flex-sheet</b> component may contain
 * a {@link wijmo.vue2.grid.sheet.WjSheet} child component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.grid.sheet.FlexSheet} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjFlexSheet = WjFlexSheetBehavior.register();
function registerV3WjFlexSheet(app) {
    app.component(WjFlexSheetBehavior.tag, WjFlexSheet);
}
var WjSheetBehavior = /** @class */ (function (_super) {
    __extends(WjSheetBehavior, _super);
    function WjSheetBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjSheetBehavior.tag = 'wj-sheet';
    WjSheetBehavior.parentProp = 'sheets';
    WjSheetBehavior.parentInCtor = true;
    WjSheetBehavior.props = [
        'wjProperty',
        'name',
        'itemsSource',
        'visible',
        'rowCount',
        'columnCount'
    ];
    WjSheetBehavior.events = [
        'initialized',
        'nameChanged'
    ];
    WjSheetBehavior.classCtor = function () { return wjcGridSheet.Sheet; };
    return WjSheetBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.grid.sheet.Sheet} class.
 *
 * The <b>wj-sheet</b> component should be contained in
 * a {@link wijmo.vue2.grid.sheet.WjFlexSheet} component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.grid.sheet.Sheet} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjSheet = WjSheetBehavior.register();
function registerV3WjSheet(app) {
    app.component(WjSheetBehavior.tag, WjSheet);
}
export function registerGridSheet(app) {
    if (VueApi.isV3Plus) {
        registerV3WjFlexSheet(app);
        registerV3WjSheet(app);
    }
}
